import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  RemoteConfig,
  getBooleanChanges,
  getNumberChanges
} from '@angular/fire/remote-config';
import { REMOTE_CONFIG, environment } from '@vaki/shared/util/environment';
import { Subscription } from 'rxjs';
import { errorTrackingRuntimeState } from './error-tracking-runtime-state';

/**
 * AC8's kill switch and AC2's "sampling configurable without a deploy",
 * on the SAME rail this repo already uses for rollout percentages
 * (`CHECKOUT_DIRECTION_C_ROLLOUT_REMOTE_CONFIG_KEY` /
 * `CHECKOUT_BLITE_ROLLOUT_REMOTE_CONFIG_KEY` in
 * libs/shared/data-access/experiment-management/…/ab-testing.service.ts:
 * "0 = kill switch (100% control, no redeploy needed)"). Flip a Remote
 * Config parameter in the Firebase console (or via the `firebase` MCP
 * server's `remoteconfig_update_template`) and it applies to the next
 * page load — no deploy, no release.
 *
 * **Not live for tabs already open.** `getBooleanChanges`/`getNumberChanges`
 * (rxfire, `@angular/fire/remote-config`) are a one-shot `_zoneWrap` over
 * `ensureInitialized(rc).then(() => getter(...))` — they emit exactly
 * once, on subscribe, from whatever `fetchAndActivate` already pulled at
 * bootstrap. There is no listener, no polling, no refetch after that.
 * A tab open before the flip keeps sending events until it reloads or is
 * closed — same one-fetch-per-load behavior as `ABTestingService`'s
 * `fetchAndActivate` warm-up above. Review send-back #3 on PR #2016
 * (Notion 3bfc94b6-2cc7-81cf-b780-f58ca1259319) found this comment
 * claiming otherwise; see the runbook's "The kill switch (AC8)" section
 * for what to do about an already-open tab during a real incident.
 *
 * Two parameters, mirrored into `errorTrackingRuntimeState` (see that
 * file's doc comment for why a plain mutable object, not DI, is what
 * `beforeSend` actually reads):
 *  - `error_tracking_kill_switch` (boolean): true = stop sending events.
 *  - `error_tracking_sample_rate_percent` (number, 0-100): what fraction
 *    of captured events actually ship. Browser volume was unknown going
 *    in (this issue's own De-risk), so this needs to move without a
 *    deploy the first time real volume is seen.
 *
 * `remoteConfig.defaultConfig` is merged (not overwritten — other
 * services share this same RemoteConfig singleton) instead of relying on
 * `getNumber`-returns-0-when-unset: without a local default, an
 * unconfigured `error_tracking_sample_rate_percent` in the RC template
 * would silently sample 0% of events, defeating "active in staging/dev
 * out of the box" from day 0 — before Nic has ever touched the RC
 * console.
 */
export const ERROR_TRACKING_KILL_SWITCH_REMOTE_CONFIG_KEY =
  'error_tracking_kill_switch';
export const ERROR_TRACKING_SAMPLE_RATE_REMOTE_CONFIG_KEY =
  'error_tracking_sample_rate_percent';

@Injectable({ providedIn: 'root' })
export class ErrorTrackingRuntimeConfigService {
  private readonly remoteConfig: RemoteConfig = inject(REMOTE_CONFIG);
  private readonly platformId = inject(PLATFORM_ID);
  private subscriptions: Subscription | null = null;

  /**
   * Starts mirroring Remote Config into `errorTrackingRuntimeState`.
   * Called once from `wireErrorTrackingRuntime()` after bootstrap — not
   * from the constructor, so the SDK never subscribes twice if something
   * else also injects this service (`providedIn: 'root'` is still a
   * singleton, but `startSyncing` is meant to run exactly once).
   */
  startSyncing(): void {
    if (!isPlatformBrowser(this.platformId) || this.subscriptions) return;

    this.remoteConfig.defaultConfig = {
      ...this.remoteConfig.defaultConfig,
      [ERROR_TRACKING_KILL_SWITCH_REMOTE_CONFIG_KEY]: false,
      [ERROR_TRACKING_SAMPLE_RATE_REMOTE_CONFIG_KEY]:
        environment.sentry.sampleRate * 100
    };

    this.subscriptions = new Subscription();
    this.subscriptions.add(
      getBooleanChanges(
        this.remoteConfig,
        ERROR_TRACKING_KILL_SWITCH_REMOTE_CONFIG_KEY
      ).subscribe({
        next: (killed) => {
          errorTrackingRuntimeState.killSwitchActive = killed;
        },
        error: (error) => {
          console.warn(
            '[ErrorTrackingRuntimeConfigService] kill switch stream failed, failing closed (killed=true):',
            error
          );
          errorTrackingRuntimeState.killSwitchActive = true;
        }
      })
    );
    this.subscriptions.add(
      getNumberChanges(
        this.remoteConfig,
        ERROR_TRACKING_SAMPLE_RATE_REMOTE_CONFIG_KEY
      ).subscribe({
        next: (percent) => {
          if (!isNaN(percent) && percent >= 0 && percent <= 100) {
            errorTrackingRuntimeState.sampleRate = percent / 100;
          }
          // Out-of-range values are ignored — errorTrackingRuntimeState
          // keeps whatever it already had (the local default, or the
          // last valid remote value).
        },
        error: (error) => {
          console.warn(
            '[ErrorTrackingRuntimeConfigService] sample rate stream failed, keeping last known rate:',
            error
          );
        }
      })
    );
  }
}
